import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Tag, Space, Input, Statistic, Row, Col, Modal, message, TreeSelect } from 'antd';
import { SearchOutlined, StockOutlined, PieChartOutlined, BarChartOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { accountApi, clientApi, groupApi, tradingApi } from '../services/api';
import { useAuth } from '../utils/auth';
import GroupedPositionPage from './GroupedPositionPage';

// 持仓分组预览：无交易权限时只读展示持仓分组（在线）；有交易权限时显示买入/清仓等操作（与分组持仓页一致）
export default function GroupedPositionPreviewPage() {
  const { user } = useAuth();
  const canTrade = user && user.trade_enabled === 1;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlineClients, setOnlineClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedPreviewGroup, setSelectedPreviewGroup] = useState(''); // 空=全部分组

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await accountApi.getGroupedPositions();
      if (res.success) setData(res.data || []);
    } catch (e) { message.error('加载失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // 加载分组（用于顶部分组筛选）
  useEffect(() => {
    groupApi.list().then(r => { if (r.success) setGroups(r.data || []); }).catch(() => {});
  }, []);

  // 非管理员默认选中自己所属分组
  useEffect(() => {
    if (user && user.is_super_admin !== 1 && user.role !== 'admin' && user.group_id) {
      setSelectedPreviewGroup(user.group_id);
    }
  }, [user]);

  // 分组树（子分组 client id 累加到父级）
  const treeGroups = useMemo(() => {
    const map = {};
    const roots = [];
    groups.forEach(g => { map[g.id] = { ...g, children: undefined, selfClientIds: g.client_id_list || [] }; });
    groups.forEach(g => {
      if (g.parent_id && map[g.parent_id]) {
        const p = map[g.parent_id];
        (p.children || (p.children = [])).push(map[g.id]);
      } else {
        roots.push(map[g.id]);
      }
    });
    const accumulate = (node) => {
      let all = [...(node.selfClientIds || [])];
      (node.children || []).forEach(ch => { accumulate(ch); all = all.concat(ch.client_id_list || []); });
      const seen = new Set();
      node.client_id_list = all.filter(id => id && !seen.has(id) && seen.add(id));
    };
    roots.forEach(accumulate);
    return roots;
  }, [groups]);

  // 当前分组范围（null=全部）
  const scopeSet = useMemo(() => {
    if (!selectedPreviewGroup) return null;
    const find = (nodes) => {
      for (const n of nodes) {
        if (n.id === selectedPreviewGroup) return n;
        if (n.children) { const f = find(n.children); if (f) return f; }
      }
      return null;
    };
    const node = find(treeGroups);
    return new Set(node?.client_id_list || []);
  }, [selectedPreviewGroup, treeGroups]);

  // 仅保留有在线客户的股票，且只显示当前分组（scopeSet）内在线客户
  const onlineData = data.map(d => ({
    ...d,
    clients: (d.clients || []).filter(c => c.online && (!scopeSet || scopeSet.has(c.client_id))),
  })).filter(d => d.clients.length > 0);
  const filtered = onlineData.filter(d =>
    !search || d.stock_code.includes(search) || d.stock_name.includes(search)
  );

  // 加载在线客户端
  useEffect(() => {
    clientApi.getOnline().then(r => { if (r.success) setOnlineClients(r.data || []); }).catch(() => {});
  }, []);

  const expandedRowRender = (record) => (
    <div style={{ background: '#1a1a2e', borderRadius: 6, padding: 4 }}>
      <Table
        dataSource={record.clients}
        rowKey="client_id"
        size="small"
        pagination={false}
        columns={[
          { title: '客户端', dataIndex: 'client_name', width: 120, render: v => v || '-' },
          { title: '持有数量', dataIndex: 'quantity', width: 80, render: (v, r) => `${(v || 0).toLocaleString()}/${(r.available_qty || 0).toLocaleString()}` },
          { title: '成本价', dataIndex: 'cost_price', width: 90, render: v => v ? `¥${v.toFixed(3)}` : '-' },
          { title: '最新价', dataIndex: 'current_price', width: 90, render: v => v ? `¥${v.toFixed(2)}` : '-' },
          { title: '持有金额', dataIndex: 'amount', width: 110, render: v => `¥${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          {
            title: '盈亏', width: 130,
            render: (_, r) => {
              if (!r.cost_price || r.cost_price <= 0) return '-';
              const pl = ((r.current_price || 0) - r.cost_price) * (r.quantity || 0);
              const plRatio = (r.current_price - r.cost_price) / r.cost_price;
              const color = pl >= 0 ? '#cf1322' : '#3f8600';
              return (
                <span style={{ color }}>
                  <span>{pl >= 0 ? '+' : ''}{pl.toFixed(2)}</span>
                  <br />
                  <span style={{ fontSize: 11 }}>{pl >= 0 ? '+' : ''}{(plRatio * 100).toFixed(2)}%</span>
                </span>
              );
            }
          },
        ]}
      />
    </div>
  );

  const columns = [
    { title: '股票代码', dataIndex: 'stock_code', width: 90 },
    { title: '股票名称', dataIndex: 'stock_name', width: 110, render: (v, r) => <a onClick={() => openKLine(r.stock_code, v)} style={{ cursor: 'pointer' }}><strong>{v}</strong></a> },
    { title: '最新价', dataIndex: 'current_price', width: 90, render: v => v ? <span style={{ fontWeight: 600, color: '#1890ff' }}>¥{v.toFixed(2)}</span> : '-' },
    { title: '持有客户', dataIndex: 'client_count', width: 80, render: v => <Tag>{v} 户</Tag> },
    { title: '总数量', dataIndex: 'total_quantity', width: 90, render: v => v?.toLocaleString() },
    { title: '总金额', dataIndex: 'total_amount', width: 120, render: v => `¥${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
  ];

  // ====== 图表数据 ======
  const chartStats = useMemo(() => {
    const totalValue = onlineData.reduce((s, g) => s + (g.clients || []).reduce((ss, c) => ss + ((c.current_price || 0) * c.quantity), 0), 0);
    let totalPL = 0, totalCost = 0;
    const pieData = onlineData.map(g => {
      const pl = g.clients?.reduce((s, c) => {
        if (!c.cost_price || c.cost_price <= 0) return s;
        return s + ((c.current_price || 0) - c.cost_price) * (c.quantity || 0);
      }, 0) || 0;
      const cost = g.clients?.reduce((s, c) => s + c.amount, 0) || 0;
      totalPL += pl;
      totalCost += cost;
      return { name: g.stock_name, value: cost, pl };
    }).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
    return { totalValue, totalPL, totalCost, plRatio: totalCost ? (totalPL / totalCost * 100) : 0, pieData };
  }, [data]);

  const pieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
    series: [{
      type: 'pie', radius: ['35%', '60%'], avoidLabelOverlap: true,
      label: { show: true, formatter: '{b}', fontSize: 10 },
      emphasis: { label: { show: true, fontWeight: 'bold' } },
      data: chartStats.pieData.map(d => ({ name: d.name, value: d.value })),
    }],
  };

  const barOption = {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}<br/>盈亏: ¥${p[0].value.toFixed(2)}` },
    grid: { left: 60, right: 20, top: 10, bottom: 25 },
    xAxis: { type: 'category', data: chartStats.pieData.map(d => d.name), axisLabel: { fontSize: 9, rotate: 30 } },
    yAxis: { type: 'value', axisLabel: { formatter: '¥{value}' } },
    series: [{
      type: 'bar',
      data: chartStats.pieData.map(d => ({
        value: Math.round(d.pl * 100) / 100,
        itemStyle: { color: d.pl >= 0 ? '#cf1322' : '#3f8600' },
      })),
    }],
  };

  // ====== K线弹窗 ======
  const [klineModal, setKlineModal] = useState({ open: false, stockCode: '', stockName: '', data: [], loading: false });
  const openKLine = async (stockCode, stockName) => {
    setKlineModal({ open: true, stockCode, stockName, data: [], loading: true });
    try {
      const res = await tradingApi.getKLine(stockCode, 200, 60);
      if (res?.success) setKlineModal(prev => ({ ...prev, data: res.data || [], loading: false }));
      else setKlineModal(prev => ({ ...prev, loading: false }));
    } catch { setKlineModal(prev => ({ ...prev, loading: false })); }
  };
  const klineOption = (() => {
    if (!klineModal.data.length) return {};
    const closes = klineModal.data.map(d => d.close_price);
    const xData = klineModal.data.map((d, i) => i % Math.ceil(closes.length / 8) === 0 ? d.date?.slice(5) || '' : '');
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
      grid: [{ left: 50, right: 20, top: 40, height: 200 }, { left: 50, right: 20, top: 270, height: 60 }],
      xAxis: [{ type: 'category', data: xData, axisLabel: { fontSize: 9 } }, { type: 'category', gridIndex: 1, data: xData, axisLabel: { fontSize: 9 } }],
      yAxis: [{ type: 'value', scale: true, splitNumber: 4 }, { type: 'value', gridIndex: 1, splitNumber: 3, axisLabel: { formatter: '{value}' } }],
      series: [
        { name: 'K线', type: 'candlestick', data: klineModal.data.map(d => [d.open_price, d.close_price, d.low_price, d.high_price]), itemStyle: { color: '#cf1322', color0: '#3f8600', borderColor: '#cf1322', borderColor0: '#3f8600' } },
        { name: '成交量', type: 'bar', xAxisIndex: 1, yAxisIndex: 1, data: klineModal.data.map((d, i) => [i, d.volume, d.open_price <= d.close_price ? '#cf1322' : '#3f8600']).map(d => ({ value: d[1], itemStyle: { color: d[2] } })) },
      ],
    };
  })();

  const groupTreeData = useMemo(() => {
    const to = (nodes) => nodes.map(n => ({
      title: `${n.name}（${(n.client_id_list || []).length}）`,
      value: n.id,
      key: n.id,
      children: n.children ? to(n.children) : undefined,
    }));
    return to(treeGroups);
  }, [treeGroups]);

  const groupFilterBar = (
    <Space>
      <span>分组筛选：</span>
      <TreeSelect
        showSearch allowClear treeDefaultExpandAll
        style={{ width: 280 }} placeholder="全部分组"
        value={selectedPreviewGroup || undefined}
        treeData={groupTreeData}
        treeNodeFilterProp="title"
        onChange={(v) => setSelectedPreviewGroup(v || '')}
      />
    </Space>
  );

  // 开启交易权限：直接复用分组持仓操作页，买入弹窗仅显示当前分组关联客户端
  if (canTrade) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>{groupFilterBar}</div>
        <GroupedPositionPage scopeClientIds={scopeSet ? [...scopeSet] : null} />
      </div>
    );
  }

  return (
    <div>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={6}>
          <Card size="small"><Statistic title="持仓总市值" value={chartStats.totalValue} precision={2} prefix="¥" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="持仓成本" value={chartStats.totalCost} precision={2} prefix="¥" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="总盈亏"
              value={Math.abs(chartStats.totalPL)} precision={2}
              prefix={chartStats.totalPL >= 0 ? '+' : '-'}
              suffix={chartStats.totalPL >= 0 ? '↑' : '↓'}
              valueStyle={{ color: chartStats.totalPL >= 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="盈亏比例"
              value={Math.abs(chartStats.plRatio)} precision={2}
              suffix="%"
              valueStyle={{ color: chartStats.plRatio >= 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={10}>
          <Card size="small" title={<Space><PieChartOutlined />持仓分布</Space>} style={{ height: 240 }}>
            <div style={{ height: 180 }}><ReactEChartsCore option={pieOption} style={{ height: 180 }} /></div>
          </Card>
        </Col>
        <Col span={14}>
          <Card size="small" title="盈亏分布（按股票）" style={{ height: 240 }}>
            <div style={{ height: 180 }}><ReactEChartsCore option={barOption} style={{ height: 180 }} /></div>
          </Card>
        </Col>
      </Row>
      <Card
        title={<Space><StockOutlined /> 持仓分组预览（在线） <Tag>{onlineData.length} 只股票</Tag></Space>}
        size="small"
        extra={
          <Space wrap>
            {groupFilterBar}
            <Input
              size="small" prefix={<SearchOutlined />}
              placeholder="搜索股票代码/名称"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: 200 }} allowClear
            />
          </Space>
        }
      >
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="stock_code"
          loading={loading}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: t => `共 ${t} 只股票` }}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
        />
      </Card>

      <Modal title={<Space><BarChartOutlined /> {klineModal.stockName}({klineModal.stockCode})</Space>}
        open={klineModal.open} footer={null} width={750}
        onCancel={() => setKlineModal({ open: false, stockCode: '', stockName: '', data: [], loading: false })}
      >
        {klineModal.loading ? <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
          : klineModal.data.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无K线数据</div>
          : <ReactEChartsCore option={klineOption} style={{ height: 380 }} />}
      </Modal>
    </div>
  );
}
